import MajorMinor from "./MajorMinor";
import SelectionState from "./SelectionState";
import { COMMIT_SELECTIONS } from "./actionTypes";
import Mode from "./Mode";
import produce from "immer";
import {
  ADD_ENTITY_ID,
  AS_OF_DATE_SET,
  BENCHMARK_ENTITY_ID_SET,
  CLEAR_ENTITY_IDS,
  DISPLAY_BENCHMARKS_SET,
  GROUP_SET,
  GROUP_TYPE_SET,
  GROUPING_LEVELS_RECEIVE,
  GROUPING_LEVELS_REQUEST,
  GROUPS_RECEIVE,
  GROUPS_REQUEST,
  MAJOR_MINOR_SET,
  RECEIVE_ENTITIES,
  REMOVE_ENTITY_ID,
  REQUEST_ENTITIES,
  SET_ENTITY_IDS,
  SET_MODE,
  SET_SCENARIOS_VALUE,
  SET_BENCHMARK_SCENARIOS_VALUE,
  SET_SIDE_BY_SIDE,
  SET_SYMBOLIC_DATE,
  LOOKTHRU_SET,
  SET_GL_PAM,
  SET_DISABLE_BMARK_WORKSPACE,
  SET_USER_INVEST_PLATFORM_IDS
} from "./actions";

const defaultSelections = {
  mode: Mode.ACC_GROUPS,
  asOfDate: null,
  groupType: null,
  groups: null,
  scenario: null,
  benchmarkScenario: null,
  sideBySide: false,
  glPam: false,
  displayBenchmarks: false,
  lookthru: true,
  benchmarkEntityId: null,
  majorMinor: MajorMinor.BOTH,
  symbolicDate: "",
  entityIds: null,
  isDisableBenchmarkWorkspace: true
};

const defaultState = {
  fetchingGroupsByGroupTypeMap: {},
  groupsByGroupTypeMap: {},
  fetchingGroupingLevels: false,
  groupingLevels: null,
  fetchingEntities: false,
  entities: null,
  userInvestPlatformIds: [],
  [SelectionState.COMMITTED]: { ...defaultSelections },
  [SelectionState.UNCOMMITTED]: { ...defaultSelections },
};

export default produce((state = defaultState, action) => {
  switch (action.type) {
    case GROUPS_REQUEST: {
      state.fetchingGroupsByGroupTypeMap[action.groupType] = true;
      delete state.groupsByGroupTypeMap[action.groupType];
      break;
    }
    case GROUPS_RECEIVE: {
      state.fetchingGroupsByGroupTypeMap[action.groupType] = false;
      state.groupsByGroupTypeMap[action.groupType] = action.data;
      break;
    }
    case GROUPING_LEVELS_REQUEST: {
      state.fetchingGroupingLevels = true;
      state.groupingLevels = null;
      break;
    }
    case GROUPING_LEVELS_RECEIVE: {
      state.fetchingGroupingLevels = false;
      state.groupingLevels = action.data;
      break;
    }
    case REQUEST_ENTITIES: {
      state.fetchingEntities = true;
      state.entities = null;
      break;
    }
    case RECEIVE_ENTITIES: {
      state.fetchingEntities = false;
      state.entities = action.data;
      break;
    }
    case AS_OF_DATE_SET: {
      state[SelectionState.UNCOMMITTED].asOfDate = action.asOfDate;
      break;
    }
    case SET_MODE: {
      state[SelectionState.UNCOMMITTED].mode = action.mode;
      /**these values should reset on mode change**/
      state[SelectionState.UNCOMMITTED].groupType = null;
      state[SelectionState.UNCOMMITTED].groups = null;
      state[SelectionState.UNCOMMITTED].entityIds = null;
      state[SelectionState.UNCOMMITTED].scenario = null;
      state[SelectionState.UNCOMMITTED].benchmarkEntityId = null;
      break;
    }
    case CLEAR_ENTITY_IDS: {
      state[SelectionState.UNCOMMITTED].entityIds =
        defaultState[SelectionState.UNCOMMITTED].entityIds;
      break;
    }
    case SET_ENTITY_IDS: {
      state[SelectionState.UNCOMMITTED].entityIds = action.value;
      break;
    }
    case ADD_ENTITY_ID: {
      if (!state[SelectionState.UNCOMMITTED].entityIds) {
        state[SelectionState.UNCOMMITTED].entityIds = [];
      }
      const { entityId } = action;
      if (Array.isArray(entityId)) {
        state[SelectionState.UNCOMMITTED].entityIds.push(...entityId);
      } else {
        state[SelectionState.UNCOMMITTED].entityIds.push(entityId);
      }
      break;
    }
    case REMOVE_ENTITY_ID: {
      state[SelectionState.UNCOMMITTED].entityIds = state[
        SelectionState.UNCOMMITTED
      ].entityIds.filter((entityId) => entityId !== action.entityId);
      break;
    }
    case SET_SYMBOLIC_DATE: {
      state[SelectionState.UNCOMMITTED].symbolicDate = action.symbolicDate;
      break;
    }
    case SET_SCENARIOS_VALUE: {
      if (!state[SelectionState.UNCOMMITTED].scenario) {
        state[SelectionState.UNCOMMITTED].scenario = [];
      }
      const { value } = action
      if (!Array.isArray(value)) {
        state[SelectionState.UNCOMMITTED].scenario.push(value);
      } else {
        state[SelectionState.UNCOMMITTED].scenario = value;
      }

      if (!action.value) {
        state[SelectionState.UNCOMMITTED].sideBySide = false;
      }
      break;
    }
    case SET_BENCHMARK_SCENARIOS_VALUE: {
      state[SelectionState.UNCOMMITTED].benchmarkScenario = action.value;
      break;
    }

    case SET_SIDE_BY_SIDE: {
      state[SelectionState.UNCOMMITTED].sideBySide = action.value;
      break;
    }
    case SET_DISABLE_BMARK_WORKSPACE: {
      state[SelectionState.UNCOMMITTED].isDisableBenchmarkWorkspace = action.value;
      break;
    }

    case GROUP_TYPE_SET: {
      state[SelectionState.UNCOMMITTED].groupType = action.groupType;
      state[SelectionState.UNCOMMITTED].groups = null;
      break;
    }
    case SET_GL_PAM: {
      state[SelectionState.UNCOMMITTED].glPam = action.value;
      break;
    }
    case GROUP_SET: {
      state[SelectionState.UNCOMMITTED].groups = action.groups;
      break;
    }
    case DISPLAY_BENCHMARKS_SET: {
      state[SelectionState.UNCOMMITTED].displayBenchmarks =
        action.displayBenchmarks;
      state[SelectionState.UNCOMMITTED].benchmarkEntityId = null;
      break;
    }
    case BENCHMARK_ENTITY_ID_SET: {
      state[SelectionState.UNCOMMITTED].benchmarkEntityId = action.entityId;
      break;
    }
    case LOOKTHRU_SET: {
      state[SelectionState.UNCOMMITTED].lookthru = action.lookthru;
      break;
    }
    case MAJOR_MINOR_SET: {
      state[SelectionState.UNCOMMITTED].majorMinor = action.majorMinor;
      break;
    }
    case SET_USER_INVEST_PLATFORM_IDS: {
      state[SelectionState.UNCOMMITTED].userInvestPlatformIds = action.value;
      break;
    }
    case COMMIT_SELECTIONS: {
      state[SelectionState.COMMITTED] = state[SelectionState.UNCOMMITTED];
      break;
    }

    default:
      return state;
  }
});
